import type { AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/vue';
import axios from '@/plugins/axios.js';
import BaseApi, { type LenientCursorPaginatedResult } from '@/api/BaseApi.ts';
import {
    ApiConversation,
    ConversationApiInterface,
    ConversationSchema,
    PaginatedConversationList,
    PaginatedMessageListWithConversation,
    PaginatedMessageListWithConversationSchema,
    ConversationNotFoundResponse,
    ConversationNotFoundResponseSchema,
    ConversationMessageResponse,
    ConversationMessageResponseSchema,
    MediaTemporaryUrlResponse,
    MediaTemporaryUrlResponseSchema,
    ToggleArchivedResponse,
    ToggleArchivedResponseSchema,
    FetchConversationsPayload,
    FetchConversationByCommunicationIdPayload,
    FindConversationByLeadPhonePayload,
    FetchMessagesPayload,
    AttachmentMessagePayload,
    TextMessagePayload,
    SendMessageResponse,
    SendMessageResponseSchema,
} from '@/api/conversations/types/index.ts';

const isAttachmentPayload = (
    payload: TextMessagePayload | AttachmentMessagePayload
): payload is AttachmentMessagePayload => {
    return 'key' in payload;
};

const MAX_INVALID_CONVERSATION_ISSUES = 20;

class ConversationAPI extends BaseApi implements ConversationApiInterface {
    name = 'ConversationsAPI';

    async fetchConversations(
        payload: FetchConversationsPayload,
        config: AxiosRequestConfig = {}
    ): Promise<PaginatedConversationList> {
        const response = await axios.get(`v1/conversations`, {
            ...config,
            params: payload,
            once: 'niotext-fetch-conversations',
        });

        const result = this._validateCursorPaginatedListLenient(ConversationSchema, response.data);

        this._captureInvalidConversationsWarning(result, payload);

        return { data: result.data, links: result.links, meta: result.meta };
    }

    async fetchConversationThread(
        conversationId: number,
        payload: FetchMessagesPayload,
        config: AxiosRequestConfig = {}
    ): Promise<PaginatedMessageListWithConversation> {
        const response = await axios.get(`v1/conversations/${conversationId}`, {
            once: 'niotext-fetch-conversation-thread',
            params: payload,
            ...config,
        });

        return this._validateCursorPaginatedResponse(
            PaginatedMessageListWithConversationSchema,
            response.data
        );
    }

    async fetchConversationByCommunicationId(
        communicationId: number,
        payload: FetchConversationByCommunicationIdPayload
    ): Promise<PaginatedMessageListWithConversation> {
        const response = await axios.get(`v1/communications/${communicationId}/conversation`, {
            params: payload,
            once: 'niotext-fetch-conversation-by-communication',
        });

        return this._validateCursorPaginatedResponse(
            PaginatedMessageListWithConversationSchema,
            response.data
        );
    }

    async fetchConversationMessage(
        conversationId: number,
        messageId: number
    ): Promise<ConversationMessageResponse> {
        const response = await axios.get(
            `v1/conversations/${conversationId}/messages/${messageId}`
        );

        return this._validateResponse(ConversationMessageResponseSchema, response.data);
    }

    async sendMessage(
        payload: TextMessagePayload | AttachmentMessagePayload
    ): Promise<SendMessageResponse> {
        const isAttachment = isAttachmentPayload(payload);
        const endpoint = isAttachment
            ? `v1/conversations/message/attachment`
            : `v1/conversations/message`;

        const response = await axios.post(endpoint, {
            ...payload,
        });

        return this._validateResponse(SendMessageResponseSchema, response.data);
    }

    async toggleArchived(conversationId: number): Promise<ToggleArchivedResponse> {
        const response = await axios.put(`v1/conversations/${conversationId}/toggle-archived`);

        return this._validateResponse(ToggleArchivedResponseSchema, response.data);
    }

    async retryMessage(
        conversationId: number,
        messageId: number
    ): Promise<ConversationMessageResponse> {
        const response = await axios.post(
            `v1/conversations/${conversationId}/messages/${messageId}/retry`
        );

        return this._validateResponse(ConversationMessageResponseSchema, response.data);
    }

    async fetchMediaTemporaryUrl(mediaId: number): Promise<MediaTemporaryUrlResponse> {
        const response = await axios.get(`v1/conversations/media/${mediaId}/temporary-url`);

        return this._validateResponse(MediaTemporaryUrlResponseSchema, response.data);
    }

    async findConversationByLeadPhone(
        payload: FindConversationByLeadPhonePayload
    ): Promise<PaginatedMessageListWithConversation | ConversationNotFoundResponse | null> {
        try {
            const response = await axios.get(`v1/conversations/find`, {
                params: payload,
            });

            return this._validateCursorPaginatedResponse(
                PaginatedMessageListWithConversationSchema,
                response.data
            );
        } catch (error: any) {
            if (error.response?.status === 404) {
                const data = error.response?.data ?? null;
                if (data) {
                    return this._validateResponse(ConversationNotFoundResponseSchema, data);
                }

                return null;
            }

            throw error;
        }
    }

    private _captureInvalidConversationsWarning(
        result: LenientCursorPaginatedResult<ApiConversation>,
        payload: FetchConversationsPayload
    ): void {
        if (!result.invalidItems.length) {
            return;
        }

        const invalid = result.invalidItems.map(({ item, issues }) => ({
            id: item?.id ?? null,
            issues,
        }));

        Sentry.captureMessage(`${this.name}: skipped invalid conversations`, {
            level: 'warning',
            contexts: {
                smsConversationValidation: {
                    accountId: payload.accountId,
                    archived: payload.archived ?? null,
                    cursor: payload.page ?? null,
                    intelligentFilter: payload.intelligentFilter ?? null,
                    invalidCount: invalid.length,
                    perPage: payload.perPage,
                    validCount: result.data.length,
                },
            },
            extra: {
                invalidConversationIds: invalid
                    .map(({ id }) => id)
                    .filter((id): id is number => typeof id === 'number'),
                issues: invalid
                    .flatMap(({ id, issues }) =>
                        issues.map((issue) => `id=${id ?? 'unknown'} ${issue}`)
                    )
                    .slice(0, MAX_INVALID_CONVERSATION_ISSUES),
            },
        });
    }
}

export default ConversationAPI;
